<template>
	<div>
		<!-- TYPE SELECTION MODAL -->
		<AnnouncementTypeModal
			v-if="showTypeModal"
			@change="setType"
			@close="showTypeModal && onClose()"
		/>

		<!-- MAIN MODAL -->
		<StandardModal
			v-if="showMainModal"
			open
			class="announcement-composer-modal"
			:title="modalTitle"
			@cancel="onCloseMain"
			ref="modal"
			:tabOrder="[
				{ css: '.selected-container.clickable', clickOnEnter: true },
				{ css: '.scheduled-date-container', clickOnEnter: true },
				{ css: '.ql-editor' },
				{ css: '.attachment-btn', clickOnEnter: true },
			]"
		>
			<div class="announcement-composer-body">
				<div
					class="selected-container content-row"
					:class="{
						clickable: isGroupType,
						empty: isGroupType && !selectedGroups.length,
					}"
					@click="onClickGroupsList"
				>
					<div
						class="selected-label"
						v-t="'announcements_new_recipienttitle'"
					/>
					<AnnouncementTargets
						:announcement="announcement"
						v-if="isOrgType || selectedGroups.length > 0"
					/>
					<div
						v-if="isGroupType && !selectedGroups.length"
						class="no-groups-selected"
					>
						{{ $t('announcements_group_select_title') }}
						<ChevronIcon right />
					</div>
				</div>

				<div
					class="scheduled-date-container content-row clickable"
					@click="showDatePicker = true"
				>
					<div
						class="scheduled-date-label"
						v-t="'announcements_scheduled_title'"
					/>
					<div
						class="scheduled-date-info"
						v-text="
							announcement.scheduled
								? formatDate(announcement.scheduled)
								: $t('announcements_scheduled_immediately')
						"
					/>
				</div>

				<div
					class="editor-container"
					:class="{ 'has-attachment': !!attachment }"
				>
					<Editor
						v-model="announcement.announcement"
						:placeholder="$t('announcements_new_placeholder')"
						disableEmojis
						disableMentions
						disableAutoResize
						enabled
						noDraft
						noClear
						allowNewLine
					/>
				</div>

				<MediaPreview
					v-if="attachment || hasMedia"
					:mediaItems="[attachment || announcement.announcementmedia[0]]"
					@remove="onRemoveAttachment"
				/>
			</div>

			<template #footer-left>
				<FilePicker
					v-if="!attachment && !hasMedia"
					class="attachment-btn"
					:class="{ remove: attachment }"
					@files="onFiles"
				>
					<PaperclipIcon type="regular" />
					{{ $t('announcements_add_attachment') }}
				</FilePicker>
				<div
					class="attachment-btn remove"
					v-if="attachment || hasMedia"
					@click="onRemoveAttachment"
				>
					<PaperclipIcon type="regular" />
					{{ $t('announcements_remove_attachment') }}
				</div>
			</template>

			<template #footer-right>
				<MessageCharLimit
					:text="announcement.announcement"
					@over="isOverMax = true"
					@under="isOverMax = false"
				/>
				<Button @click="onCloseMain">
					{{ $t('common_button_cancel') }}
				</Button>
				<Button
					primary
					@click="saveAnnouncement"
					:disabled="disabled"
					:loading="isLoading"
				>
					{{ confirmText }}
				</Button>
			</template>
		</StandardModal>

		<!-- GROUP SELECTION MODAL -->
		<SelectionModal
			v-if="showGroupSelection"
			:title="$t('announcements_group_select_title')"
			:confirmText="'Select'"
			:items="matchedGroups"
			:columns="columns"
			:selectedItemsById="selectedGroupsById"
			:allowEmptySelection="selectedGroups.length > 0"
			:large="false"
			skipDiscard
			class="select-groups-modal"
			@selection="onGroupSelectionChange"
			@confirm="saveGroupSelection"
			@cancel="showGroupSelection = false"
			ref="groupModal"
		>
			<template #cell="{ data }">
				<div class="group-name">
					<div
						v-if="data.category"
						class="category"
						v-text="data.category.displayname"
					/>
					<div class="group" v-text="data.displayname" />
				</div>
			</template>
			<template #filters>
				<div class="filters">
					<Input
						:placeholder="$t('contacts_userpicker_search_placeholder')"
						class="search"
						v-model="searchQuery"
					/>
				</div>
			</template>
		</SelectionModal>

		<!-- SUCCESS MODAL -->
		<StandardModal
			v-if="showSuccessModal"
			open
			small
			class="announcement-success-modal"
			:title="$t('common_confirmation_sent_nocheck')"
			:confirmText="$t('announcements_view')"
			hideCancel
			@confirm="onClickView"
			@cancel="onClose"
		>
			<div class="announcement-success-content">
				<CheckIcon halo />
				<div v-t="'announcements_has_been_sent'" />
			</div>
		</StandardModal>

		<!-- DISCARD CHANGES MODAL -->
		<StandardModal
			v-if="showDiscardModal"
			open
			small
			class="announcement-discard-modal"
			:title="$t('announcements_discard_changes')"
			:confirmText="$t('admin_common_discard_button_title')"
			:confirmProps="{ danger: true }"
			:cancelText="$t('announcements_keep_editing')"
			@confirm="onClose"
			@cancel="showDiscardModal = false"
		>
			<div class="announcement-discard-content">
				<TrashCanIcon halo />
				<div v-t="'announcements_cancel_confirmation'" />
			</div>
		</StandardModal>

		<!-- SCHEDULED DATE PICKER -->
		<DatePickerModal
			v-if="showDatePicker"
			v-model="announcement.scheduled"
			showTime
			noPastDates
			:cancelText="
				announcement.scheduled ? $t('announcements_unschedule') : null
			"
			@set="setScheduledDate"
			@cancel="announcement.scheduled = null"
			@close="showDatePicker = false"
		/>
	</div>
</template>

<script>
import {
	StandardModal,
	Input,
	Button,
	FilePicker,
	DatePickerModal,
} from '@/components/common'
import {
	getGroupChats,
	sortChatsByDisplayName,
	getCategory,
	getOrganization,
	getChat,
	getAnnouncement,
	canSendAnnouncements,
} from '@/store'
import SelectionModal from '@/components/OrgManagement/common/SelectionModal'
import MessageCharLimit from '@/components/MessageInput/MessageCharLimit'
import MediaPreview from '@/components/MessageInput/MediaPreview'
import Editor from '@/components/Editor'
import AnnouncementTypeModal, { types } from './AnnouncementTypeModal'
import AnnouncementTargets from './AnnouncementTargets'
import escapeRegEx from 'escape-string-regexp'
import { PaperclipIcon, CheckIcon, TrashCanIcon, ChevronIcon } from '@/icons'
import {
	saveAnnouncement,
	prepAndUploadAttachment,
	log,
} from '@/api/announcement'
import { chatCan, CREATE_ANNOUNCEMENT } from '@/helpers/permissions'

export { types }

export default {
	name: 'AnnouncementComposer',
	components: {
		StandardModal,
		SelectionModal,
		Input,
		Button,
		Editor,
		PaperclipIcon,
		FilePicker,
		MediaPreview,
		MessageCharLimit,
		DatePickerModal,
		AnnouncementTypeModal,
		AnnouncementTargets,
		CheckIcon,
		TrashCanIcon,
		ChevronIcon,
	},
	props: {
		editId: [String, Number],
		type: {
			type: String,
			validator: (val) => Object.values(types).includes(val),
		},
	},

	data() {
		const announcement = Object.assign({}, getAnnouncement(this.editId))
		return {
			announcement,
			isSaving: false,
			isUploading: false,
			allGroups: this.getAllGroups(),
			columns: [{ field: 'displayname' }],
			selectedGroupsById: {},
			showGroupSelection: false,
			showSuccessModal: false,
			showDiscardModal: false,
			searchQuery: '',
			attachment: null,
			isOverMax: false,
			showDatePicker: false,
		}
	},

	created() {
		this.$onEventBus({
			'chat-update': () => {
				this.allGroups = this.getAllGroups()
			},
		})
	},

	mounted() {
		if (!canSendAnnouncements()) {
			return this.onClose()
		}
		if (!this.showTypeModal) {
			this.setType(this.isOrgType ? types.ORG : types.GROUP)
		}
	},

	computed: {
		modalTitle() {
			if (this.editId) return this.$t('announcements_edit_title')
			return this.isGroupType
				? this.$t('announcements_type_group_title')
				: this.$t('announcements_type_org_title')
		},
		disabled() {
			if (!this.announcement.announcement?.trim() || this.isOverMax) return true
			if (this.isGroupType) return !this.selectedGroups.length
			return false
		},
		isLoading() {
			return this.isSaving || this.isUploading
		},
		isScheduled() {
			return !!this.announcement.scheduled
		},
		isOrgType() {
			if (this.type && this.type === types.ORG) return true
			return !!this.announcement.targets?.organization_id
		},
		isGroupType() {
			if (this.type && this.type === types.GROUP) return true
			return !!this.announcement.targets?.bubble_ids
		},
		hasType() {
			return this.isOrgType || this.isGroupType
		},
		hasMedia() {
			return !!this.announcement.announcementmedia?.length
		},
		showTypeModal() {
			return !this.hasType
		},
		showMainModal() {
			return this.hasType && !this.showSuccessModal
		},
		confirmText() {
			if (this.isUploading) {
				return this.$t('announcements_uploading_file')
			}
			if (this.isSaving) {
				return this.isScheduled
					? this.$t('announcements_scheduling_hud')
					: this.$t('announcements_sending_hud')
			}
			if (this.editId) {
				return this.$t('common_button_save')
			}
			return this.isScheduled
				? this.$t('announcements_schedule')
				: this.$t('announcements_send_now')
		},
		matchedGroups() {
			if (!this.searchQuery) return this.allGroups
			const regex = new RegExp(escapeRegEx(this.searchQuery), 'i')
			return this.allGroups.filter(
				(g) => regex.test(g.displayname) || regex.test(g.category?.displayname)
			)
		},
		selectedGroups() {
			if (!this.isGroupType) return []
			const { bubble_ids = [] } = this.announcement?.targets || {}
			return bubble_ids?.map(getChat)
		},
	},

	watch: {
		selectedGroups(chats = []) {
			if (!chats.length) {
				this.selectedGroupsById = {}
				return
			}
			this.selectedGroupsById = chats.reduce((acc, chat) => {
				acc[chat.id] = { item: chat, selected: true }
				return acc
			}, {})
		},
	},

	methods: {
		getAllGroups() {
			return getGroupChats()
				.filter((g) => chatCan(g, CREATE_ANNOUNCEMENT))
				.map((g) => ({ ...g, category: getCategory(g.category_id) }))
				.sort(sortChatsByDisplayName)
		},

		onGroupSelectionChange(selected = {}) {
			this.selectedGroupsById = selected
		},

		saveGroupSelection() {
			const bubble_ids = Object.values(this.selectedGroupsById)
				.filter((x) => x.selected === true)
				.map((x) => x.item)
				.sort(sortChatsByDisplayName)
				.map((g) => g.id)

			this.announcement = Object.assign({}, this.announcement, {
				targets: { bubble_ids },
			})
			this.showGroupSelection = false
		},

		async onFiles(files) {
			const normalized = await prepAndUploadAttachment(files[0])
			this.attachment = normalized
		},

		onRemoveAttachment() {
			this.attachment = null
			Object.assign(this.announcement, { announcementmedia: [] })
		},

		setScheduledDate(scheduled) {
			this.announcement = Object.assign({}, this.announcement, { scheduled })
		},

		setType(type) {
			const bubble_ids = this.announcement?.targets?.bubble_ids ?? []
			let targets = { bubble_ids }
			if (type === types.ORG) {
				const org = getOrganization()
				targets = { organization_id: org.id }
			}
			this.announcement = Object.assign({}, this.announcement, { targets })
		},

		formatDate(date) {
			return this.$m.utc(date).local().format('LLL')
		},

		async saveAnnouncement() {
			try {
				log('starting to save...')
				if (this.attachment) {
					if (!this.attachment.isComplete) {
						log('has attachment that is still uploading...')
						this.isUploading = true
						this.$onEventBus('upload-change', (media) => {
							if (
								media?.tempId === this.attachment.tempId &&
								media.isComplete &&
								this.isUploading
							) {
								log('upload complete, continuing save...', media)
								this.attachment.isComplete = true
								this.isUploading = false
								this.saveAnnouncement()
							} else {
								log('still uploading...', media)
							}
						})
						return
					}
					Object.assign(this.announcement, {
						announcementmedia: [this.attachment],
					})
				}
				this.isSaving = true
				const data = await saveAnnouncement(this.announcement)
				this.isSaving = true
				log('save successful', data)
				if (this.editId) {
					this.$alert.success(this.$t('common_confirmation_updated_nocheck'))
					this.onClose()
				} else if (data.announcement.scheduled) {
					this.$alert.success(this.$t('announcements_filter_scheduled'))
					this.newAnnouncement = data.announcement
					this.onClickView()
				} else {
					this.showSuccessModal = true
					this.newAnnouncement = data.announcement
				}
			} catch (e) {
				console.error(e)
				this.isUploading = false
				this.isSaving = false
				this.$refs.modal.$alert.error(e)
			}
		},

		onClickView() {
			const announcement = this.newAnnouncement
			this.newAnnouncement = null
			this.$router.replace(`/announcements/${announcement.id}`)
			this.onClose()
		},

		onClickGroupsList() {
			if (!this.isGroupType) return
			this.showGroupSelection = true
		},

		hasDiscardableChanges() {
			// this is an annoying amount of logic just to determine if there are changes :(
			if (this.attachment) return true
			const { announcement, targets = {}, scheduled } = this.announcement

			if (this.editId) {
				const orig = getAnnouncement(this.editId)
				if (orig.announcement.trim() !== announcement.trim()) return true
				if (this.formatDate(orig.scheduled) !== this.formatDate(scheduled))
					return true
				if (this.isGroupType) {
					if (targets.bubble_ids?.length !== orig.targets.bubble_ids?.length)
						return true
					if (
						JSON.stringify(targets.bubble_ids) !==
						JSON.stringify(orig.targets.bubble_ids)
					)
						return true
				}
				return false
			}

			if (announcement || scheduled) return true
			if (this.isGroupType && targets.bubble_ids?.length) return true

			return false
		},

		onCloseMain() {
			if (this.hasDiscardableChanges()) {
				this.showDiscardModal = true
				return
			}
			this.onClose()
		},

		onClose() {
			this.$emit('close')
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.announcement-composer-body {
	height: 100%;
	@include flex-column;

	.content-row {
		height: 55px;
		padding: 0 16px;
		width: 100%;
		border-bottom: 1px solid var(--neutral-200);
		max-width: 100%;
		overflow: hidden;
		@include flex(16px);
		flex-shrink: 0;
		&.clickable {
			cursor: pointer;
		}
	}

	:deep(.selected-container) {
		&.empty {
			justify-content: space-between;
		}
		.selected-label {
			@include fontFamily(800);
		}
		.announcement-targets {
			max-width: calc(100% - 42px);
			gap: 12px;
		}
		.no-groups-selected {
			@include flex(4px);
			color: var(--neutral-600);
			.pronto-icon {
				color: var(--neutral-400);
			}
		}
	}
	.scheduled-date-container {
		.scheduled-date-label {
			@include fontFamily(800);
		}
		.scheduled-date-info {
			@include fontFamily(400);
			padding: 4px 8px;
			border-radius: 6px;
			color: var(--blue-300);
			background: var(--blue-100);
		}
	}
}

.editor-container {
	flex-grow: 1;
	transition: max-height ease-in-out 0.3s;
	@include flex-column;
	overflow: auto;

	.pronto-editor {
		flex-grow: 1;
		height: 100% !important;
		padding: 0;
		max-height: unset !important;
		--max-height: 100% !important;
		--padding: 0 !important;

		:deep(.ql-editor) {
			max-height: 100% !important;
			padding: 16px;
			&::before {
				left: 16px;
			}
		}
		:deep(.emoji_completions) {
			max-height: 100px;
			overflow-y: auto;
		}
	}
}

.media-preview-container {
	&.expanded {
		padding: 8px 16px 16px 16px;
		border-top: 1px solid var(--neutral-200);
	}
}

.attachment-btn {
	cursor: pointer;
	@include fontFamily(900);
	color: var(--blue-300);
	@include flex(8px);
	&.remove {
		color: var(--red-300);
	}
	.pronto-icon {
		font-size: 16px;
	}
}

.group-name {
	font-family: Avenir;
	font-style: normal;
	color: var(--neutral-800);

	.category {
		font-weight: normal;
		font-size: 12px;
		line-height: 16px;
		text-transform: uppercase;
		@include ellipsis;
	}

	.group {
		font-weight: 800;
		font-size: 15px;
		line-height: 20px;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.announcement-composer-modal {
	.modal-footer-right {
		@include flex(16px);
		.pronto-btn {
			margin-left: 0 !important;
		}
	}
}

.announcement-discard-content {
	@include flex-column-center(16px);
	justify-content: center;
	height: 100%;
	.pronto-icon {
		font-size: 60px;
		color: var(--red-300);
	}
}

.announcement-success-content {
	@include flex-column-center(16px);
	justify-content: center;
	height: 100%;
	.pronto-icon {
		font-size: 60px;
		color: var(--blue-300);
	}
}

@include overrideScheme('dark') {
	.announcement-composer-body {
		.content-row {
			border-color: var(--neutral-300) !important;
		}
		.scheduled-date-container {
			.scheduled-date-info {
				background: var(--blue-200);
				color: white;
			}
		}
	}
}

@include overrideContrast {
	.announcement-composer-body {
		.scheduled-date-container {
			.scheduled-date-info {
				color: white;
			}
		}
	}
}
</style>
